import React, { useRef, useState } from 'react';
import { CheckboxItem, Text, Tooltip } from '..';
import { colorSet } from '@theme';
import { IOptionList, IOptionListItem } from '@types';
import { Box, Popover } from '@mui/material';
import { ChipPaperType, styleConstant, StandardFacialExpressionChildOptionValueEnum, PathnameEnum } from '@constant';
import {
  FaceSatisfiedIcon,
  FaceCoquettishIcon,
  FaceJoyIcon,
  FaceNeutralIcon,
  FaceSurprisedIcon,
  FacePensiveIcon,
  FaceDisgustIcon,
  FaceAngryIcon,
  FaceEmbarrassedIcon,
  FaceFearIcon,
  FaceSadnessIcon,
  FaceCautiousIcon,
  FaceFrustrationIcon,
  FaceShamelessIcon,
  FaceShameIcon,
  FaceSuspicionIcon,
  FaceAnxietyIcon,
  FaceScreamIcon,
  XBlackIcon,
  ToggleOnIcon,
  ToggleOffIcon,
  FaceSmilingIcon,
  FaceLaughingIcon,
  FaceShyIcon,
  FaceSeriousIcon
} from '@assets/iconComponents';
import { useLocation } from 'react-router';

interface IPaperSelectProps {
  label: string;
  options: IOptionList;
  selectedOptions: IOptionListItem[];
  onNoneToggle: (isToggleOn: boolean) => void;
  onSelect: (option: IOptionListItem) => void;
  paperType: ChipPaperType;
  onFacialExpressionCategoryClick: (category: 'positive' | 'neutral' | 'negative', isCategoryChecked: boolean) => void;
  chipRef: React.RefObject<HTMLDivElement>;
  isAdminPage: boolean;
  onClose: () => void;
  open: boolean;
}

// 표정 카테고리 정의
const FACE_CATEGORIES = {
  positive: {
    label: '긍정',
    options: {
      만족: {
        name: '만족',
        value: StandardFacialExpressionChildOptionValueEnum['만족'],
        isSelected: false
      },
      미소: {
        name: '미소',
        value: StandardFacialExpressionChildOptionValueEnum['미소'],
        isSelected: false
      },
      새침: {
        name: '새침',
        value: StandardFacialExpressionChildOptionValueEnum['새침'],
        isSelected: false
      },
      기쁨: {
        name: '기쁨',
        value: StandardFacialExpressionChildOptionValueEnum['기쁨'],
        isSelected: false
      },
      폭소: {
        name: '폭소',
        value: StandardFacialExpressionChildOptionValueEnum['폭소'],
        isSelected: false
      },
      수줍음: {
        name: '수줍음',
        value: StandardFacialExpressionChildOptionValueEnum['수줍음'],
        isSelected: false
      }
    }
  },
  neutral: {
    label: '중립',
    options: {
      무표정: {
        name: '무표정',
        value: StandardFacialExpressionChildOptionValueEnum['무표정'],
        isSelected: false
      },
      놀람: {
        name: '놀람',
        value: StandardFacialExpressionChildOptionValueEnum['놀람'],
        isSelected: false
      },
      고민: {
        name: '고민',
        value: StandardFacialExpressionChildOptionValueEnum['고민'],
        isSelected: false
      },
      진지: {
        name: '진지',
        value: StandardFacialExpressionChildOptionValueEnum['진지'],
        isSelected: false
      }
    }
  },
  negative: {
    label: '부정',
    options: {
      혐오: {
        name: '혐오',
        value: StandardFacialExpressionChildOptionValueEnum['혐오'],
        isSelected: false
      },
      좌절: {
        name: '좌절',
        value: StandardFacialExpressionChildOptionValueEnum['좌절'],
        isSelected: false
      },
      분노: {
        name: '분노',
        value: StandardFacialExpressionChildOptionValueEnum['분노'],
        isSelected: false
      },
      뻔뻔: {
        name: '뻔뻔',
        value: StandardFacialExpressionChildOptionValueEnum['뻔뻔'],
        isSelected: false
      },
      당황: {
        name: '당황',
        value: StandardFacialExpressionChildOptionValueEnum['당황'],
        isSelected: false
      },
      수치: {
        name: '수치',
        value: StandardFacialExpressionChildOptionValueEnum['수치'],
        isSelected: false
      },
      공포: {
        name: '공포',
        value: StandardFacialExpressionChildOptionValueEnum['공포'],
        isSelected: false
      },
      의심: {
        name: '의심',
        value: StandardFacialExpressionChildOptionValueEnum['의심'],
        isSelected: false
      },
      슬픔: {
        name: '슬픔',
        value: StandardFacialExpressionChildOptionValueEnum['슬픔'],
        isSelected: false
      },
      불안: {
        name: '불안',
        value: StandardFacialExpressionChildOptionValueEnum['불안'],
        isSelected: false
      },
      경계: {
        name: '경계',
        value: StandardFacialExpressionChildOptionValueEnum['경계'],
        isSelected: false
      },
      절규: {
        name: '절규',
        value: StandardFacialExpressionChildOptionValueEnum['절규'],
        isSelected: false
      }
    }
  }
};

// 표정 옵션별 아이콘 매핑
const FACE_ICON_MAP = {
  만족: FaceSatisfiedIcon,
  미소: FaceSmilingIcon,
  새침: FaceCoquettishIcon,
  기쁨: FaceJoyIcon,
  폭소: FaceLaughingIcon,
  수줍음: FaceShyIcon,
  무표정: FaceNeutralIcon,
  놀람: FaceSurprisedIcon,
  고민: FacePensiveIcon,
  진지: FaceSeriousIcon,
  혐오: FaceDisgustIcon,
  분노: FaceAngryIcon,
  당황: FaceEmbarrassedIcon,
  공포: FaceFearIcon,
  슬픔: FaceSadnessIcon,
  경계: FaceCautiousIcon,
  좌절: FaceFrustrationIcon,
  뻔뻔: FaceShamelessIcon,
  수치: FaceShameIcon,
  의심: FaceSuspicionIcon,
  불안: FaceAnxietyIcon,
  절규: FaceScreamIcon
} as const;

const PaperSelect: React.FC<IPaperSelectProps> = (props: IPaperSelectProps) => {
  const {
    label,
    options,
    selectedOptions,
    onNoneToggle,
    onSelect,
    paperType,
    onFacialExpressionCategoryClick,
    chipRef,
    isAdminPage,
    onClose,
    open
  } = props;

  const paperSelectRef = useRef<HTMLDivElement>(null);

  const isNoneSelected = selectedOptions.some((option) => option.value === 'NONE');

  // 위치 계산 함수
  const getAnchorOrigin = (): { vertical: 'top' | 'bottom' | 'center'; horizontal: 'left' | 'right' | 'center' } => {
    if (paperType === ChipPaperType.FACE_CHECKBOX_LIST && isAdminPage) {
      return { vertical: 'top', horizontal: 'right' };
    }
    return { vertical: 'bottom', horizontal: 'left' };
  };

  const getTransformOrigin = (): { vertical: 'top' | 'bottom' | 'center'; horizontal: 'left' | 'right' | 'center' } => {
    if (paperType === ChipPaperType.FACE_CHECKBOX_LIST && isAdminPage) {
      return { vertical: 'bottom', horizontal: 'right' };
    }
    return { vertical: 'top', horizontal: 'left' };
  };

  const handleSelectClick = (option: IOptionListItem) => {
    onSelect(option);
    // SELECT 타입은 클릭 후 닫기
    if (paperType === ChipPaperType.SELECT) {
      onClose();
    }
    // SELECT가 아닌 경우는 열린 상태 유지
  };

  const handleCheckboxClick = (option: IOptionListItem) => {
    onSelect(option);
  };

  const handleFaceCheckboxClick = (option: IOptionListItem) => {
    onSelect(option);
  };

  const paperSelectContent = (
    <div
      ref={paperSelectRef}
      style={{
        padding: '6px 0px',
        backgroundColor: colorSet.white,
        boxShadow: '0px 5px 25px rgba(0, 0, 0, 0.1)',
        borderRadius: '0px'
      }}
    >
      {paperType === ChipPaperType.SELECT ? (
        <>
          {Object.keys(options).map((optionKey, index) => (
            <Box
              id="select-menu-item"
              key={index}
              onClick={(event) => {
                event.stopPropagation();
                handleSelectClick(options[optionKey]);
              }}
              sx={{
                width: 120,
                padding: '6px 20px',
                cursor: 'pointer',
                backgroundColor: selectedOptions.some((opt) => opt.value === options[optionKey].value)
                  ? 'rgba(119, 78, 255, 0.15)'
                  : colorSet.white,
                ':hover': {
                  backgroundColor: selectedOptions.some((opt) => opt.value === options[optionKey].value)
                    ? 'rgba(119, 78, 255, 0.15)'
                    : colorSet.gray100,
                  '& .optionListItemText': {
                    fontWeight: 'bold'
                  }
                }
              }}
            >
              <Text className="optionListItemText" variant="body1">
                {optionKey}
              </Text>
            </Box>
          ))}
        </>
      ) : null}

      {paperType === ChipPaperType.CHECKBOX_LIST ? (
        <>
          {Object.keys(options).map((optionKey, index) => (
            <div
              id="checkbox-menu-item"
              key={index}
              onClick={(event) => {
                event.stopPropagation();
                handleCheckboxClick(options[optionKey]);
              }}
              style={{ width: 148, padding: '6px 20px' }}
            >
              <CheckboxItem
                label={optionKey}
                isChecked={selectedOptions.some((opt) => opt.value === options[optionKey].value)}
                onChange={() => {
                  handleCheckboxClick(options[optionKey]);
                }}
              />
            </div>
          ))}
        </>
      ) : null}

      {paperType === ChipPaperType.CHECKBOX_LIST_TOGGLE ? (
        <div style={{ width: 170 }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '6px 12px',
              borderBottom: `1px solid ${colorSet.gray200}`
            }}
          >
            <Text variant="body1" color={colorSet.gray300}>
              {label}
            </Text>
            <div
              onClick={() => {
                onNoneToggle(isNoneSelected);
              }}
              style={{ cursor: 'pointer' }}
            >
              {isNoneSelected ? <ToggleOffIcon /> : <ToggleOnIcon />}
            </div>
          </div>
          <div style={{ position: 'relative' }}>
            {Object.keys(options).map((optionKey, index) => (
              <div
                id="checkbox-menu-item"
                key={index}
                onClick={(event) => {
                  event.stopPropagation();
                  handleCheckboxClick(options[optionKey]);
                }}
                style={{ padding: '6px 20px' }}
              >
                <CheckboxItem
                  label={optionKey}
                  isChecked={selectedOptions.some((opt) => opt.value === options[optionKey].value)}
                  onChange={() => {
                    handleCheckboxClick(options[optionKey]);
                  }}
                />
              </div>
            ))}
            {isNoneSelected ? (
              <div
                style={{
                  width: '100%', // 부모 div의 너비를 채우도록 설정
                  height: '100%', // 부모 div의 높이를 채우도록 설정
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.5)',
                  zIndex: styleConstant.zIndexChipPaper + 1 // 클릭 방지
                }}
              />
            ) : null}
          </div>
        </div>
      ) : null}

      {paperType === ChipPaperType.FACE_CHECKBOX_LIST ? (
        <div
          id="face-checkbox-menu-item"
          onClick={(event) => event.stopPropagation()}
          style={{
            width: 564
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '6px 12px',
              borderBottom: `1px solid ${colorSet.gray200}`
            }}
          >
            <Text variant="body1" color={colorSet.gray300}>
              {label}
            </Text>
            <div
              onClick={() => {
                onNoneToggle(isNoneSelected);
              }}
              style={{ cursor: 'pointer' }}
            >
              {isNoneSelected ? <ToggleOffIcon /> : <ToggleOnIcon />}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'row', position: 'relative' }}>
            {/* 긍정 카테고리 */}
            <div style={{ flex: 1 }}>
              <div style={{ padding: '6px 20px', backgroundColor: colorSet.gray100 }}>
                <CheckboxItem
                  label="긍정"
                  isChecked={Object.values(FACE_CATEGORIES.positive.options).every((opt) =>
                    selectedOptions.some((selected) => selected.value === opt.value)
                  )}
                  onChange={() => {
                    onFacialExpressionCategoryClick(
                      'positive',
                      Object.values(FACE_CATEGORIES.positive.options).every((opt) =>
                        selectedOptions.some((selected) => selected.value === opt.value)
                      )
                    );
                  }}
                />
              </div>
              {Object.keys(FACE_CATEGORIES.positive.options).map((optionKey, index) => {
                const IconComponent = FACE_ICON_MAP[optionKey as keyof typeof FACE_ICON_MAP];
                return (
                  <div key={index} style={{ padding: '6px 20px' }}>
                    <CheckboxItem
                      label={optionKey}
                      imageComponent={<IconComponent style={{ width: 32, height: 32 }} />}
                      isChecked={selectedOptions.some(
                        (opt) =>
                          opt.value ===
                          FACE_CATEGORIES.positive.options[optionKey as keyof typeof FACE_CATEGORIES.positive.options]
                            ?.value
                      )}
                      onChange={() =>
                        handleFaceCheckboxClick(
                          FACE_CATEGORIES.positive.options[optionKey as keyof typeof FACE_CATEGORIES.positive.options]
                        )
                      }
                    />
                  </div>
                );
              })}
            </div>

            {/* 중립 카테고리 */}
            <div style={{ flex: 1 }}>
              <div style={{ padding: '6px 20px', backgroundColor: colorSet.gray100 }}>
                <CheckboxItem
                  label="중립"
                  isChecked={Object.values(FACE_CATEGORIES.neutral.options).every((opt) =>
                    selectedOptions.some((selected) => selected.value === opt.value)
                  )}
                  onChange={() => {
                    onFacialExpressionCategoryClick(
                      'neutral',
                      Object.values(FACE_CATEGORIES.neutral.options).every((opt) =>
                        selectedOptions.some((selected) => selected.value === opt.value)
                      )
                    );
                  }}
                />
              </div>
              {Object.keys(FACE_CATEGORIES.neutral.options).map((optionKey, index) => {
                const IconComponent = FACE_ICON_MAP[optionKey as keyof typeof FACE_ICON_MAP];
                return (
                  <div key={index} style={{ padding: '6px 20px' }}>
                    <CheckboxItem
                      label={optionKey}
                      imageComponent={<IconComponent style={{ width: 32, height: 32 }} />}
                      isChecked={selectedOptions.some(
                        (opt) =>
                          opt.value ===
                          FACE_CATEGORIES.neutral.options[optionKey as keyof typeof FACE_CATEGORIES.neutral.options]
                            ?.value
                      )}
                      onChange={() =>
                        handleFaceCheckboxClick(
                          FACE_CATEGORIES.neutral.options[optionKey as keyof typeof FACE_CATEGORIES.neutral.options]
                        )
                      }
                    />
                  </div>
                );
              })}
            </div>

            {/* 부정 카테고리 */}
            <div style={{ flex: 2 }}>
              <div style={{ padding: '6px 20px', backgroundColor: colorSet.gray100 }}>
                <CheckboxItem
                  label="부정"
                  isChecked={Object.values(FACE_CATEGORIES.negative.options).every((opt) =>
                    selectedOptions.some((selected) => selected.value === opt.value)
                  )}
                  onChange={() => {
                    onFacialExpressionCategoryClick(
                      'negative',
                      Object.values(FACE_CATEGORIES.negative.options).every((opt) =>
                        selectedOptions.some((selected) => selected.value === opt.value)
                      )
                    );
                  }}
                />
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr'
                }}
              >
                {Object.keys(FACE_CATEGORIES.negative.options).map((optionKey, index) => {
                  const IconComponent = FACE_ICON_MAP[optionKey as keyof typeof FACE_ICON_MAP];
                  return (
                    <div key={index} style={{ padding: '6px 20px' }}>
                      <CheckboxItem
                        label={optionKey}
                        imageComponent={<IconComponent style={{ width: 32, height: 32 }} />}
                        isChecked={selectedOptions.some(
                          (opt) =>
                            opt.value ===
                            FACE_CATEGORIES.negative.options[optionKey as keyof typeof FACE_CATEGORIES.negative.options]
                              ?.value
                        )}
                        onChange={() =>
                          handleFaceCheckboxClick(
                            FACE_CATEGORIES.negative.options[optionKey as keyof typeof FACE_CATEGORIES.negative.options]
                          )
                        }
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            {isNoneSelected ? (
              <div
                style={{
                  width: '100%', // 부모 div의 너비를 채우도록 설정
                  height: '100%', // 부모 div의 높이를 채우도록 설정
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.5)',
                  zIndex: styleConstant.zIndexChipPaper + 1 // 클릭 방지
                }}
              />
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );

  return (
    <Popover
      open={open}
      anchorEl={chipRef.current}
      onClose={onClose}
      anchorOrigin={getAnchorOrigin()}
      transformOrigin={getTransformOrigin()}
      slotProps={{
        paper: {
          onMouseDown: (event) => {
            event.stopPropagation();
          },
          sx: {
            marginTop: paperType === ChipPaperType.FACE_CHECKBOX_LIST && isAdminPage ? '-8px' : '8px',
            marginLeft: '0px',
            marginRight: '0px',
            boxShadow: '0px 5px 25px rgba(0, 0, 0, 0.1)',
            borderRadius: '0px',
            zIndex: styleConstant.zIndexChipPaper,
            maxWidth: 'calc(100vw - 16px)',
            maxHeight: 'calc(100vh - 16px)'
          }
        }
      }}
    >
      {paperSelectContent}
    </Popover>
  );
};

// XButton 컴포넌트
export const ChipXButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    onClick={(event) => {
      event.stopPropagation();
      onClick();
    }}
    style={{
      border: 'none',
      backgroundColor: 'transparent',
      cursor: 'pointer',
      padding: 0,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      marginLeft: 16
    }}
  >
    <XBlackIcon style={{ width: 14, height: 14 }} />
  </button>
);

// Chip 컴포넌트
interface IChipProps {
  label: string;
  needTooltip?: boolean;
  tooltipText?: string;
  paperType?: ChipPaperType;
  options: IOptionList;
  storedChildOptionValue: IOptionList | null;
  setSelectedOption: (param: IOptionList | null) => void;
  isError?: boolean;
  disabled?: boolean;
}

export const Chip: React.FC<IChipProps> = ({
  label,
  needTooltip = false,
  tooltipText = '',
  paperType = ChipPaperType.SELECT,
  options,
  storedChildOptionValue,
  setSelectedOption,
  isError = false,
  disabled = false
}) => {
  const [isActive, setIsActive] = useState(false);
  const chipRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const locationPathname = location.pathname;
  const isAdminPage =
    locationPathname.includes(PathnameEnum.ADMIN_IMAGE_UPLOAD_PAGE) ||
    locationPathname.includes(PathnameEnum.ADMIN_IMAGE_EDIT_ROOT);

  const selectedOptions = storedChildOptionValue
    ? Object.values(storedChildOptionValue).filter((option) => option.isSelected)
    : [];

  const handleChipClick = () => {
    if (disabled) return;

    // Chip 클릭 시 PaperSelect 닫기 (요구사항 10)
    if (isActive) {
      setIsActive(false);
      return;
    }

    const newIsActive = !isActive;
    setIsActive(newIsActive);
  };

  const handleNoneToggle = (isNoneSelected: boolean) => {
    let updatedOptions: IOptionList | null;

    // Toggle OFF(없음 true) -> Toggle ON(없음 false)으로 변경시
    if (isNoneSelected) {
      updatedOptions = null;
    }
    // Toggle ON(없음 false) -> Toggle OFF(없음 true)로 변경시
    else {
      updatedOptions = {
        없음: { name: '없음', value: 'NONE', isSelected: true }
      };
    }

    setSelectedOption(updatedOptions);
  };

  const handleOptionSelect = (option: IOptionListItem) => {
    let updatedOptions: IOptionList | null;

    if (paperType === ChipPaperType.SELECT) {
      updatedOptions = Object.keys(options).reduce((acc, key) => {
        acc[key] = {
          ...options[key],
          isSelected: key === option.name
        };
        return acc;
      }, {} as IOptionList);
    } else if (
      paperType === ChipPaperType.CHECKBOX_LIST ||
      paperType === ChipPaperType.CHECKBOX_LIST_TOGGLE ||
      paperType === ChipPaperType.FACE_CHECKBOX_LIST
    ) {
      const tempUpdatedOptions = {
        ...storedChildOptionValue,
        [option.name]: {
          ...(storedChildOptionValue?.[option.name] ?? option),
          isSelected: !storedChildOptionValue?.[option.name]?.isSelected
        }
      };

      const allFalse = Object.values(tempUpdatedOptions).every((opt) => !opt.isSelected);
      updatedOptions = allFalse ? null : tempUpdatedOptions;
    } else {
      updatedOptions = null;
    }

    setSelectedOption(updatedOptions);
  };

  const handleFacialExpressionCategoryClick = (
    category: 'positive' | 'neutral' | 'negative',
    isCategoryChecked: boolean
  ) => {
    const options = Object.values(FACE_CATEGORIES[category].options);
    const tempUpdatedOptions = { ...storedChildOptionValue };

    options.forEach((option) => {
      tempUpdatedOptions[option.name] = {
        ...(tempUpdatedOptions?.[option.name] ?? option),
        isSelected: !isCategoryChecked
      };
    });

    const allFalse = Object.values(tempUpdatedOptions).every((opt) => !opt.isSelected);
    const updatedOptions = allFalse ? null : tempUpdatedOptions;

    setSelectedOption(updatedOptions);
  };

  const handleResetChip = () => {
    setIsActive(false);
    setSelectedOption(null);
  };

  const handleClose = () => {
    setIsActive(false);
  };

  return (
    <div ref={chipRef} style={{ position: 'relative', width: 'fit-content', whiteSpace: 'nowrap' }}>
      {selectedOptions.length === 0 ? (
        <div
          style={{
            height: 38,
            padding: '9px 16px',
            display: 'inline-flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: '12px',
            border: `1px solid ${isActive ? colorSet.primaryMain : isError ? colorSet.errorMain : colorSet.gray300}`,
            backgroundColor: disabled ? colorSet.gray200 : isActive ? 'rgba(119, 78, 255, 0.15)' : 'transparent',
            cursor: disabled ? 'default' : 'pointer'
          }}
          onClick={handleChipClick}
        >
          {needTooltip && <Tooltip text={tooltipText} />}
          <Text variant="body1" color={disabled ? colorSet.gray400 : colorSet.gray600}>
            {label}
          </Text>
        </div>
      ) : (
        <div
          style={{
            height: 38,
            padding: '9px 16px',
            display: 'inline-flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: '12px',
            border: `1px solid ${isActive ? colorSet.primaryMain : colorSet.gray600}`,
            backgroundColor: isActive ? 'rgba(119, 78, 255, 0.15)' : colorSet.gray200,
            cursor: 'pointer'
          }}
          onClick={handleChipClick}
        >
          {needTooltip && <Tooltip text={tooltipText} />}
          <Text variant="body1" fontWeight="bold" style={{ marginRight: 8 }}>
            {label}
          </Text>
          <Text variant="body1">
            {selectedOptions
              .slice(0, 2)
              .map((option) => option.name)
              .join(', ')}
          </Text>
          {selectedOptions.length > 2 ? (
            <Text variant="body1" color={colorSet.gray400}>
              &nbsp;{`+${selectedOptions.length - 2} more`}
            </Text>
          ) : null}
          <ChipXButton onClick={handleResetChip} />
        </div>
      )}
      <PaperSelect
        label={label}
        options={options}
        selectedOptions={selectedOptions}
        onNoneToggle={handleNoneToggle}
        onSelect={handleOptionSelect}
        paperType={paperType}
        onFacialExpressionCategoryClick={handleFacialExpressionCategoryClick}
        chipRef={chipRef}
        isAdminPage={isAdminPage}
        onClose={handleClose}
        open={isActive}
      />
    </div>
  );
};
